/**
 * Centralized access to the build-time `import.meta.env.VITE_*` values.
 *
 * Everything in this module is the FRONTEND'S view of its configuration
 * — values are inlined into the bundle at build time, so they are PUBLIC
 * (visible to anyone with browser devtools). Real secrets must never
 * live here; they belong on the backend.
 *
 * Why this exists rather than reading `import.meta.env.X` everywhere:
 *   1. One place to spot a missing/misspelled var (the rest of the code
 *      gets a typed value, not `string | undefined`).
 *   2. One place to do defaulting / coercion (e.g. boolean parsing).
 *   3. Easy to mock in tests without monkey-patching `import.meta`.
 *   4. Future swap of build tool (Vite → something else) becomes a
 *      one-file change, not a global codemod.
 */

interface ViteEnv {
  /** Build-target environment label: "dev" | "production" | etc. */
  ENVIRONMENT: string;
  /**
   * Full URL of the betright-backend API root, ending in `/api/v1`.
   * NOT optional — booting without it is a misconfigured deployment
   * and we'd rather fail loudly than silently make CORS requests to
   * `undefined`.
   */
  API_URL: string;
  /**
   * VAPID public key for Web Push subscriptions. Empty string means
   * push is disabled for this build (the pushNotifications util
   * detects this and silently no-ops, per its design).
   */
  VAPID_PUBLIC_KEY: string;
  /**
   * Sentry DSN. Empty string disables Sentry entirely (the initSentry
   * call no-ops). Production deployments MUST set this; the dev .env
   * deliberately leaves it blank so local debugging doesn't pollute
   * the issue tracker.
   */
  SENTRY_DSN: string;
}

function required(key: string, value: string | undefined): string {
  if (value === undefined || value === "") {
    throw new Error(
      `Missing required environment variable VITE_${key}. ` +
        `Set it in .env (or .env.local) and restart the dev server. ` +
        `See .env.example for the full list.`,
    );
  }
  return value;
}

export const env: ViteEnv = {
  ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT ?? "dev",
  API_URL: required("API_URL", import.meta.env.VITE_API_URL),
  VAPID_PUBLIC_KEY: import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "",
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN ?? "",
};
